<template>
  <v-container v-if="cliente" fluid class="pa-4">

    <!-- Identidad -->
    <div class="d-flex align-center flex-wrap mb-6" style="gap: 16px">
      <div class="avatar-grande" :class="{ 'avatar-grande--empresa': esEmpresa }">
        <v-icon v-if="esEmpresa" color="indigo lighten-2" size="28">mdi-domain</v-icon>
        <span v-else>{{ iniciales }}</span>
      </div>
      <div class="minw-0">
        <div class="d-flex align-center flex-wrap" style="gap: 8px">
          <h2 class="text-h5 font-weight-bold">{{ nombreCompleto }}</h2>
          <v-chip x-small outlined :color="esEmpresa ? 'indigo lighten-2' : 'blue-grey lighten-2'">
            <v-icon left x-small>{{ esEmpresa ? 'mdi-domain' : 'mdi-account' }}</v-icon>
            {{ esEmpresa ? 'Empresa' : 'Persona' }}
          </v-chip>
          <v-chip v-if="cliente.es_empleado" x-small outlined color="teal lighten-2">Empleado</v-chip>
        </div>
        <div class="caption grey--text mt-1">{{ condicionIva }}</div>
      </div>
      <v-spacer />
      <v-btn outlined small @click="goToEditar">
        <v-icon left small>mdi-pencil</v-icon>
        Editar
      </v-btn>
    </div>

    <v-row>
      <!-- Identificación fiscal: lo que se necesita para facturarle -->
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title class="subtitle-1 font-weight-bold pb-1">
            {{ esEmpresa ? 'Datos de la empresa' : 'Datos personales' }}
          </v-card-title>
          <v-card-text class="pt-0">
            <template v-if="esEmpresa">
              <div class="dato">
                <span class="grey--text caption">Razón social</span>
                <span class="body-2">{{ cliente.razonSocial || sinCargar }}</span>
              </div>
            </template>
            <template v-else>
              <div class="dato">
                <span class="grey--text caption">Nombre</span>
                <span class="body-2">{{ nombre || sinCargar }}</span>
              </div>
              <div class="dato">
                <span class="grey--text caption">Apellido</span>
                <span class="body-2">{{ apellido || sinCargar }}</span>
              </div>
            </template>
            <div class="dato">
              <span class="grey--text caption">{{ esEmpresa ? 'CUIT' : 'Documento' }}</span>
              <span class="body-2">
                {{ documento || sinCargar }}
                <span v-if="documento && tipoDocumento" class="grey--text caption ml-1">
                  ({{ tipoDocumento }})
                </span>
              </span>
            </div>
            <div class="dato">
              <span class="grey--text caption">Condición frente al IVA</span>
              <span class="body-2">{{ condicionIva }}</span>
            </div>
            <v-alert
              v-if="!documento"
              dense
              text
              type="info"
              class="caption mt-3 mb-0"
            >
              Sin {{ esEmpresa ? 'CUIT' : 'documento' }} sólo se le puede emitir
              comprobante a consumidor final.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Contacto -->
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title class="subtitle-1 font-weight-bold pb-1">Contacto</v-card-title>
          <v-card-text class="pt-0">
            <div class="dato">
              <span class="grey--text caption">
                <v-icon x-small color="grey">mdi-phone-outline</v-icon>
                Teléfono
              </span>
              <span class="body-2">{{ telefono || sinCargar }}</span>
            </div>
            <div class="dato">
              <span class="grey--text caption">
                <v-icon x-small color="grey">mdi-email-outline</v-icon>
                Email
              </span>
              <span class="body-2 text-truncate">{{ email || sinCargar }}</span>
            </div>
            <div class="dato">
              <span class="grey--text caption">
                <v-icon x-small color="grey">mdi-map-marker-outline</v-icon>
                Localidad
              </span>
              <span class="body-2">{{ localidad || sinCargar }}</span>
            </div>
            <div v-if="!telefono && !email" class="caption grey--text mt-3">
              No hay forma de contactarlo cargada.
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { fetchCliente } from "@/mixins/cliente/fetchCliente";

export default {
  name: "cliente-home",
  mixins: [fetchCliente],

  data() {
    return {
      sinCargar: "Sin cargar",
      // ClienteSerializer devuelve estos dos como id: se resuelven contra las
      // tablas de referencia para mostrar el nombre.
      tiposDocumento: [],
      condicionesIva: [],
    };
  },

  computed: {
    esEmpresa() {
      return this.cliente && this.cliente.es_persona_fisica === false;
    },
    // El dato puede estar en el Cliente o en su Persona según por dónde se dio
    // de alta: se lee uno y se cae al otro.
    nombre() {
      if (!this.cliente) return "";
      return this.cliente.nombre || this.cliente.persona.nombre || "";
    },
    apellido() {
      if (!this.cliente) return "";
      return this.cliente.apellido || this.cliente.persona.apellido || "";
    },
    nombreCompleto() {
      if (!this.cliente) return "";
      if (this.esEmpresa && this.cliente.razonSocial) return this.cliente.razonSocial;
      const partes = [this.nombre, this.apellido].filter(Boolean);
      return partes.join(" ") || "Sin nombre";
    },
    iniciales() {
      const partes = this.nombreCompleto.split(/[\s,]+/).filter(Boolean);
      if (!partes.length) return "?";
      if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
      return (partes[0][0] + partes[1][0]).toUpperCase();
    },
    documento() {
      if (!this.cliente) return "";
      return this.cliente.documento || this.cliente.persona.documento || "";
    },
    tipoDocumento() {
      const t = this.cliente && (this.cliente.tipo_documento || this.cliente.persona.tipo_documento);
      if (!t) return "";
      if (typeof t === "object") return t.description || "";
      const encontrado = this.tiposDocumento.find((x) => x.id === t);
      return encontrado ? encontrado.description : "";
    },
    telefono() {
      if (!this.cliente) return "";
      return this.cliente.telefono || this.cliente.persona.telefono || "";
    },
    email() {
      if (!this.cliente) return "";
      return this.cliente.email || this.cliente.persona.email || "";
    },
    condicionIva() {
      const c = this.cliente && this.cliente.condicionIva;
      if (!c) return "Sin definir";
      if (typeof c === "object") return c.nombre || "Sin definir";
      const encontrada = this.condicionesIva.find((x) => x.id === c);
      return encontrada ? encontrada.nombre : "Sin definir";
    },
    localidad() {
      const loc = (this.cliente && (this.cliente.localidad || this.cliente.persona.localidad)) || null;
      if (!loc) return "";
      return loc.provincia ? `${loc.nombre}, ${loc.provincia.nombre}` : loc.nombre;
    },
  },

  methods: {
    async cargarReferencias() {
      try {
        const [tipos, condiciones] = await Promise.all([
          this.axios.get("/clientes/tipodocumento"),
          this.axios.get("/clientes/condicioniva"),
        ]);
        this.tiposDocumento = tipos.data;
        this.condicionesIva = condiciones.data;
      } catch (e) {
        console.error(e);
      }
    },

    goToEditar() {
      const esPos = this.$route.path.includes("/pos");
      this.$router.push({
        name: esPos ? "pos.cliente.editar" : "empresa.cliente.editar",
        params: { id_cliente: this.$route.params.id_cliente },
      });
    },
  },

  created() {
    this.cargarReferencias();
  },
};
</script>

<style scoped>
.dato {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.dato:last-of-type {
  border-bottom: none;
}

.avatar-grande {
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: 50%;
  background: rgba(33, 150, 243, 0.18);
  color: #64b5f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
}

.avatar-grande--empresa {
  background: rgba(92, 107, 192, 0.18);
}

.minw-0 {
  min-width: 0;
}
</style>
